import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiArrowLeft } from "@mdi/js";
import { toast } from "react-toastify";
import {
  BACK,
  UPDATE_LABEL,
  SUBMIT_LABEL,
  PROJECT_NAME_LABEL,
  IMAGE_ONE_MB_TOAST,
  ONLY_IMAGES_ALLOWED_TOAST,
  ADD_PROJECT,
  EDIT_PROJECT,
  ADD_PROJECT_TITLE,
  UPDATING_LABEL,
  SUBMITTING_LABEL,
} from "Components/constants/commonNames";
import {
  REQUEST_ADD_PROJECT_COMMON,
  REQUEST_EDIT_PROJECT_COMMON,
  REQUEST_FILE_UPLOAD_COMMON,
  REQUEST_VIEW_PROJECT_COMMON,
} from "API/Services/common";
import BaseButton from "../../Components/Common/BaseButton";
import BaseInput from "../../Components/Common/BaseInput";
import { useFormik, FormikValues } from "formik";
import * as Yup from "yup";
import {
  InputPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import "../../STYLE/ProductsPage.css";
import { checkStatusCodeSuccess } from "Components/constants/enums";
import { BaseImageURL } from "API/Services/services";
import NoImageFound from "../../Assets/images/NoImageFound.png";

interface ProjectData {
  id: number;
  project_name: string;
  projectImages: { project_image: string }[];
}

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const ProjectId = id ? Number(id) : undefined;
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [viewProjectData, setviewProjectData] = useState<ProjectData | null>(
    null
  );
  const [imagesToShow, setImagesToShow] = useState(4);

  const validationSchema = Yup.object().shape({
    project_name: Yup.string().required(
      validationMessages.required(PROJECT_NAME_LABEL)
    ),
    project_images: Yup.array()
      .min(1, "Product image is required")
      .required("Product image is required"),
  });

  const handleSubmit = async (values: FormikValues) => {
    setLoader(true);

    const errors = await formik.validateForm();

    if (errors.project_images) {
      toast.error(errors.project_images);
      setLoader(false);
      return;
    }

    const payload = {
      project_name: values.project_name,
      projectImageDetail: values.project_images.map((image: string) => ({
        project_image: image,
      })),
    };

    try {
      const resp = ProjectId
        ? await REQUEST_EDIT_PROJECT_COMMON(ProjectId, payload)
        : await REQUEST_ADD_PROJECT_COMMON(payload);
      if (checkStatusCodeSuccess(resp.statusCode)) {
        toast.success(resp.message);
        navigate("/projects");
      } else {
        toast.error(resp?.message);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setLoader(false);
    }
  };

  const formik: any = useFormik({
    initialValues: {
      project_name: "",
      project_images: [] as string[],
    },
    validationSchema,
    onSubmit: handleSubmit,
    validateOnBlur: false,
    validateOnChange: false,
  });

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const inputElement = event.target as HTMLInputElement;
    const files = inputElement.files;
    const maxSizeInBytes = 1024 * 1024;

    if (files) {
      formik.setFieldTouched("project_images", true);

      setFullScreenLoader(true);
      const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];
      const newImages: string[] = [];
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (file.size > maxSizeInBytes) {
          toast.error(IMAGE_ONE_MB_TOAST);
          inputElement.value = "";
          setFullScreenLoader(false);
          return;
        }

        if (!allowedImageTypes.includes(file.type)) {
          toast.error(ONLY_IMAGES_ALLOWED_TOAST);
          inputElement.value = "";
          setFullScreenLoader(false);
          return;
        }

        newImages.push(URL.createObjectURL(file));
        formData.append("files", file);
      }

      REQUEST_FILE_UPLOAD_COMMON(formData)
        .then((resp: any) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const uploadedImagePaths = resp?.data.map(
              (fileData: any) => fileData.file
            );
            formik.setFieldValue("project_images", [
              ...formik.values.project_images,
              ...uploadedImagePaths,
            ]);
            inputElement.value = "";
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((error: any) => {
          toast.error(error?.response?.data?.message || error.message);
        })
        .finally(() => {
          setFullScreenLoader(false);
        });
    }
  };

  const handleViewProject = async (id: any) => {
    setFullScreenLoader(true);
    try {
      const resp: any = await REQUEST_VIEW_PROJECT_COMMON(id);
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        setviewProjectData(resp?.data);
        toast.success(resp.message);
      } else {
        toast.error(resp.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setFullScreenLoader(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleViewProject(id);
    }
  }, [id]);

  useEffect(() => {
    if (
      viewProjectData &&
      formik.values.project_name !== viewProjectData.project_name
    ) {
      formik.setValues({
        project_name: viewProjectData.project_name || "",
        project_images:
          viewProjectData.projectImages?.map((image) => image.project_image) ||
          [],
      });
    }
  }, [viewProjectData]);

  document.title = ADD_PROJECT_TITLE;

  const adjustImagesToShow = () => {
    const width = window.innerWidth;
    if (width <= 576) {
      setImagesToShow(2);
    } else if (width <= 768) {
      setImagesToShow(2);
    } else if (width <= 992) {
      setImagesToShow(3);
    } else {
      setImagesToShow(4);
    }
  };

  useEffect(() => {
    adjustImagesToShow();
    window.addEventListener("resize", adjustImagesToShow);
    return () => window.removeEventListener("resize", adjustImagesToShow);
  }, []);

  const ImageCarousel = React.memo(
    ({
      images,
      remove,
    }: {
      images: string[];
      remove: (index: number) => void;
    }) => {
      const [startIndex, setStartIndex] = useState(0);

      const fallbackImage = NoImageFound;

      const next = () => {
        if (startIndex + imagesToShow < images.length) {
          setStartIndex(startIndex + 1);
        }
      };

      const previous = () => {
        if (startIndex > 0) {
          setStartIndex(startIndex - 1);
        }
      };

      return (
        <div className="d-flex align-items-center">
          {startIndex > 0 && (
            <div className="mdi mdi-arrow-left" onClick={previous}></div>
          )}
          <div className="d-flex">
            {images
              ?.slice(startIndex, startIndex + imagesToShow)
              ?.map((image, index) => (
                <div key={index} className="card position-relative mb-0  mx-1">
                  <img
                    src={`${BaseImageURL}${image}`}
                    alt={`img-${index}`}
                    className="img-fluid img-container"
                    onError={(e) => (e.currentTarget.src = fallbackImage)}
                  />
                  <i
                    className="position-absolute mdi mdi-close fs-4 text-dark cursor-pointer top-0 end-0"
                    onClick={() => remove(startIndex + index)}
                  ></i>
                </div>
              ))}
          </div>
          {startIndex + imagesToShow < images.length && (
            <i className="mdi mdi-arrow-right" onClick={next}></i>
          )}
        </div>
      );
    }
  );

  return (
    <div className="top-div">
      <div className="container-fluid">
        <Row className="align-items-center mt-2 mb-1">
          <Col md={6} sm={12}>
            <span className="fs-4 mx-2">{id ? EDIT_PROJECT : ADD_PROJECT}</span>
          </Col>
          <Col md={6}>
            <BaseButton
              type="button"
              onClick={() => navigate("/projects")}
              text={
                <>
                  <Icon path={mdiArrowLeft} size={0.7} className="me-2" />
                  {BACK}
                </>
              }
              color="info"
              className="mt-0 w-auto float-end"
            />
          </Col>
        </Row>

        <Card>
          <CardBody className="p-4">
            <form
              onSubmit={formik.handleSubmit}
              className="d-flex w-100 flex-column"
            >
              <div className="mb-2">
                <Row>
                  <Col xs={12} sm={6} md={4} className="mb-0">
                    <BaseInput
                      required={true}
                      label={PROJECT_NAME_LABEL}
                      name="project_name"
                      className="mb-3"
                      type="text"
                      placeholder={InputPlaceHolder(PROJECT_NAME_LABEL)}
                      value={formik.values.project_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.errors.project_name}
                      touched={formik.touched.project_name}
                    />
                  </Col>

                  <Col xs={12} sm={6} md={4} className="mb-0">
                    <BaseInput
                      required={true}
                      label="Project Images"
                      accept="image/*"
                      multiple={true}
                      tooltip={true}
                      tooltipIcon="help-circle-outline"
                      tooltipIconColor="info"
                      tooltipText="Only JPEG/PNG Allowed"
                      name="project_images"
                      type="file"
                      onChange={handleFileChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.project_images &&
                        formik.errors.project_images
                      }
                      touched={formik.touched.project_images}
                    />

                    {formik.values.project_images.length > 0 && (
                      <div className="mt-2">
                        <ImageCarousel
                          images={formik.values.project_images}
                          remove={(index: number) => {
                            formik.setFieldValue(
                              "project_images",
                              formik.values.project_images.filter(
                                (_: any, i: number) => i !== index
                              )
                            );
                          }}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
              </div>

              <div className="d-flex justify-content-end">
                <BaseButton
                  type="submit"
                  text={
                    id
                      ? loader
                        ? UPDATING_LABEL
                        : UPDATE_LABEL
                      : loader
                      ? SUBMITTING_LABEL
                      : SUBMIT_LABEL
                  }
                  color="success"
                  className="mt-0 w-auto px-4 py-2"
                  size="sm"
                  loading={loader}
                />
              </div>
            </form>
          </CardBody>
        </Card>
        {fullScreenLoader && (
          <div className="full-screen-loader">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
